<template>
  <div>
    <a-dropdown placement="bottomRight" :trigger="['click']" @click="handleTransferClick">
      <a-tooltip title="Transferir ticket">
        <a-button type="text" :disabled="!portfolio" class="button-transfer" @click.prevent>
          <SwapOutlined class="icon" />
          Transferir ticket
        </a-button>
      </a-tooltip>

      <template #overlay>
        <a-menu @click="handleDropdownClick" :style="{ width: '220px !important' }">
          <a-menu-item key="queues-users">
            <a-popover placement="right" :overlayClassName="'popover-option'">
              <template #content>
                <span>Transfiere el ticket a un usuario de esta</span><br />
                <span>u otra cola de la misma campaña</span>
              </template>
              <template #title>
                <span>Transferir por cola y usuario</span>
              </template>
              <span>Transferir por cola y usuario</span>
            </a-popover>
          </a-menu-item>
          <a-menu-item key="users" :disabled="!queue">
            <a-popover
              placement="right"
              title="Transferir a un usuario"
              :overlayClassName="'popover-option'"
            >
              <template #content>
                <span>Transfiere el ticket a otra usuario de la</span><br />
                <span>misma campaña</span>
              </template>
              <template #title>
                <span>Transferir a un usuario</span>
              </template>
              <span>Transferir a un usuario</span>
            </a-popover>
          </a-menu-item>
          <a-menu-item key="queues">
            <a-popover
              placement="right"
              title="Transferir a cola"
              :overlayClassName="'popover-option'"
            >
              <template #content>
                <span>Transfiere el ticket a otra cola de la</span><br />
                <span>misma campaña</span>
              </template>
              <template #title>
                <span>Transferir a cola</span>
              </template>
              <span>Transferir a cola</span>
            </a-popover>
          </a-menu-item>
          <TypificationModal
            ref="refTypificationModalUser"
            transferUser
            class="button button-typification"
            :defaultFormState="formStateTypification"
            :isChat="isChat"
            @handleNext="nextTransfer"
          />
          <TypificationModal
            ref="refTypificationModalQueues"
            transferQueue
            class="button button-typification"
            :defaultFormState="formStateTypification"
            :isChat="isChat"
            @handleNext="nextTransfer"
          />
        </a-menu>
      </template>
    </a-dropdown>

    <QueueUserTransferModal
      v-if="openQueueUserTransferModal"
      v-model:open="openQueueUserTransferModal"
      :origin="1"
      :queues="transferData.queues"
      :ticketIdList="[ticket.id]"
      :ticket="ticket"
      :portfolio="ticket?.portfolio"
      :entity="ticket?.portfolio?.entity"
      :defaultSelected="queueIdSelected"
      :inCall="effectiveInCall"
      :callRinging="isCallRinging"
      @update="handleTransferredTicket"
      @transferCall="transferCall"
    />

    <QueueTransferModal
      v-if="openQueueTransferModal"
      v-model:open="openQueueTransferModal"
      :origin="1"
      :queues="transferData.queues"
      :ticketIdList="[ticket.id]"
      :ticket="ticket"
      :portfolio="ticket?.portfolio"
      :entity="ticket?.portfolio?.entity"
      :fromTypification="fromTypification"
      :defaultSelected="queueIdSelected"
      :inCall="effectiveInCall"
      :callRinging="isCallRinging"
      @update="handleTransferredTicket"
      @typificationTransfer="typificationTransfer"
      @handleBack="backTypification"
      @transferCall="transferCall"
    />

    <UserTransferModal
      v-if="openUserTransferModal"
      v-model:open="openUserTransferModal"
      :origin="1"
      :users="transferData.agents"
      :queue="queue"
      :ticketIdList="[ticket.id]"
      :ticket="ticket"
      :fromTypification="fromTypification"
      :defaultSelected="userIdSelected"
      :isLoading="isLoadingUsers"
      :inCall="effectiveInCall"
      :callRinging="isCallRinging"
      @update="handleTransferredTicket"
      @typificationTransfer="typificationTransfer"
      @handleBack="backTypification"
      @transferCall="transferCall"
      @clearIntervalUsers="clearIntervalUsers"
      @setIntervalUsers="setIntervalUsers"
    />
  </div>
</template>

<script setup>
import { useStore } from "vuex";
import { SwapOutlined } from "@ant-design/icons-vue";
import { computed, ref, toRaw, onMounted, watch, onUnmounted, inject } from "vue";
import { useJssip } from "@/app/shared/composables/useJssip";
import EventBus from "@/app/shared/utils/eventBus";
import { WorkspaceEvent } from "@/app/workspace/domain/events";
import { CallStatus } from "@/packages/vue-jssip/jssip/phone";

import QueueUserTransferModal from "@/app/ticketsManager/components/modals/transfer/QueueUserTransferModal.vue";
import QueueTransferModal from "@/app/ticketsManager/components/modals/transfer/QueueTransferModal.vue";
import UserTransferModal from "@/app/ticketsManager/components/modals/transfer/UserTransferModal.vue";
import TypificationModal from "@/app/workspace/components/modal/TypificationModal.vue";
import { useRoute, useRouter } from "vue-router";
import { message } from "ant-design-vue";
import { useSaveTypificationManagement } from "@/app/workspace/composables/useSaveTypificationManagement";
import * as Sentry from "@sentry/vue";
import { ModeFetchWorkspace } from "../../utils/enums";

const props = defineProps({
  ticket: { type: Object, required: true },
  isWorkspace: { type: Boolean, required: false, default: false },
  isChat: { type: Boolean, required: false, default: false },
  inCall: { type: Boolean, required: false },
});

const $jssip = useJssip();
const store = useStore();
const route = useRoute();
const router = useRouter();
const { saveTypificationManagement: persistTypificationManagement } =
  useSaveTypificationManagement();

// Solo llamada ESTABLECIDA (InCall) habilita la transferencia de llamada (to_transfer);
// en timbrado/conexión la transferencia queda bloqueada.
const isCallEstablished = computed(() => $jssip.phone.status === CallStatus.InCall);
const isCallRinging = computed(() => {
  return (
    $jssip.phone.status === CallStatus.Connecting ||
    $jssip.phone.status === CallStatus.Ringing
  );
});
const effectiveInCall = computed(() => props.inCall && isCallEstablished.value);

const interval = ref(null);
const isLoadingUsers = ref(false);

const refTypificationModalUser = ref(null);
const refTypificationModalQueues = ref(null);
const pendingTypificationModal = ref(null);

const transferData = computed(() => store.getters["getTransferData"]);

const portfolio = computed(() => props.ticket.portfolio?.id);

const queue = computed(() => props.ticket.queue?.name);

const usersMonitoring = computed(() => store.getters["getMonitorUserList"]);

const openQueueUserTransferModal = ref(false);
const openQueueTransferModal = ref(false);
const openUserTransferModal = ref(false);

const emit = defineEmits(["modalIsOpen"]);

const fetchTicket = inject("fetchTicket", null);

const fromTypification = ref(false);

const handleTransferClick = async () => {
  // await store.dispatch("fetchTransferData", { ticket: props.ticket.id, origin: 1 });
};

const handleCreateReminder = async () => {
  const formData = toRaw(formStateTypification.value.reminderRef.formState);

  const data = {
    title: formData.title,
    date_schedule: formData.date_schedule,
    time_schedule: formData.date_schedule + " " + formData.time_schedule,
    description: formData.description || "-",
    client: Number(route.query.client),
    ticket: Number(route.query.ticket),
    interaction_id: Number(route.query.interaction),
  };

  try {
    await store.dispatch("addReminder", data);
    message.success("Se creó el recordatorio correctamente");
  } catch (error) {
    message.error("Hubo un error al crear el recordatorio");
  }
};

const typificationTransfer = async (done) => {
  await saveTypificationManagement();
  done();
}

const saveTypificationManagement = async () => {
  if (formStateTypification.value.hasReminder) await handleCreateReminder();

  const typification = formStateTypification.value;
  const ticket = Number(route.query.ticket);

  // if (localStorage.getItem('workspace') == 'romero') {
  //   const logSentry = {
  //     strategy_phone: typification.$jssip.phone.meta.strategyPhone,
  //     ticketQuery: Number(route.query.ticket),
  //   }
  //   Sentry.captureMessage(`Save Management transfer: ${JSON.stringify(logSentry)} | ${Date.now()}`, {
  //     level: "info",
  //     extra: management,
  //   });
  // }

  // Funcion reutilizable para guardar tipificacion y para guardar tipificacion y transferir llamada
  const result = await persistTypificationManagement({
    typification,
    ticket,
    interactionId: route.query.interaction,
    isInCall: props.inCall,
    isCurrentTicket: $jssip.phone.isCurrentCallTicket(ticket),
    // En "tipificar y transferir" la llamada la termina la transferencia, no el guardado: si se
    // colgara aquí, el cliente iría a la encuesta y la transferencia solo movería el ticket.
    saveManagementAndFinishCall: false,
    ticketTypification: ticket,
    routeTicket: route.query.ticket,
    observations: typification?.comment,
    strategyPhone: typification.$jssip.phone.meta.strategyPhone,
    deleteTypificationFromLocalStorage: () =>
      pendingTypificationModal.value?.deleteTypificationFromLocalStorage(),
  });

  if (result?.saved) message.success("Se ha guardado la tipificación correctamente");
};

const handleTransferredTicket = async () => {
  // if (fromTypification.value) {
  //   await saveTypificationManagement();
  // }
  console.log("fetchActiveWorkspaceTickets handleTransferredTicket SingleTransfer");
  store.dispatch("fetchActiveWorkspaceTickets", {
    mode: ModeFetchWorkspace.RefreshByTicket,
    ticketId: props.ticket.id,
  });
  if (fetchTicket) {
    await fetchTicket({ preventLoading: true });
  }
  // EventBus.$emit(WorkspaceEvent.RESET_LIST);
  router.push("/workspace");
};

const formStateTypification = ref(null);
const userIdSelected = ref(null);
const queueIdSelected = ref(null);

const nextTransfer = ({ key, typificationRef, formState, fromTypificationParam = true }) => {
  console.log(formState, "nextTransfer");
  pendingTypificationModal.value = typificationRef;
  console.log(pendingTypificationModal.value, "typificationRef set");
  formStateTypification.value = { ...formState };
  handleDropdownClick({ key: key, fromTypificationParam: fromTypificationParam });
};

const backTypification = async ({ key, selected }) => {
  if (key == "users") {
    await refTypificationModalUser.value?.handleShowModal();
    await refTypificationModalUser.value?.fetchFormFields();
    userIdSelected.value = selected;
  } else if (key == "queues") {
    await refTypificationModalQueues.value?.handleShowModal();
    await refTypificationModalQueues.value?.fetchFormFields();
    queueIdSelected.value = selected;
  }
};

const transferCall = async ({ itemId, type, queueId, userId }) => {
  if (effectiveInCall.value) {
    console.log("TRANSFERIR CALL", itemId, type);
    let annex = "";
    const body = { tickets: [props.ticket.id], is_call: true, user: null };

    // transferencia a agente o transferencia a cola y agente seleccionado
    if (type === "agent" || (type === "queue-user" && itemId)) {
      const item = usersMonitoring.value.find((el) => el.user.id == itemId);
      annex = `r${item.annex.split("@")[0]}`;
      body.user = Number(itemId);
    }

    // transferencia a cola
    if (type === "queue") {
      annex = `q${itemId}`;
    }

    // transferencia a cola y agente aleatorio
    if (type === "queue-user" && !itemId) {
      annex = `q${queueId}`;
    }

    try {
      if (type === "agent" || (type === "queue-user" && itemId)) {
        // Validar en backend que el agente destino esté en línea antes de transferir la llamada
        await store.dispatch('validateAgentTransfer', body);
      }
      $jssip.transfer(annex, type === "agent" || type === "queue-user" ? itemId : undefined);
      $jssip.end({ fromTransfer: true });
      $jssip.setCallStatus(false);
      $jssip.reset();
      EventBus.$emit(WorkspaceEvent.TRANSFER_CALL, { ticket: props.ticket.id });
      store.commit("SET_ACW_STATE", false);
      store.commit("SET_TICKET_TRANSFERED", props.ticket.id);
    } catch (error) {
      message.destroy();
      const errorMessage = error?.data?.user?.[0] 
        || error?.data?.user_agent?.[0]
        || error?.data?.tickets?.[0]
        || "Ocurrió un error al transferir la llamada";
      message.error(errorMessage);
      console.log("error modal transfer",error);
    }
  } 
};

const fetchTransferData = async (onlyUsers = false) => {
  await store.dispatch("fetchTransferData", {
    ticket: props.ticket.id,
    origin: 1,
    onlyUsers: onlyUsers,
  });
};

const clearIntervalUsers = () => {
  console.log("clearIntervalUsers");
  clearInterval(interval.value);
  interval.value = null;
};

const setIntervalUsers = () => {
  if (!interval.value) {
    interval.value = setInterval(async () => {
      if (openUserTransferModal.value) {
        await fetchTransferData(true);
      }
      isLoadingUsers.value = false;
    }, 7000);
  }
};

watch(
  openUserTransferModal,
  async (value) => {
    if (value) {
      isLoadingUsers.value = true;
      await fetchTransferData(true);
      isLoadingUsers.value = false;
      setIntervalUsers();
    } else {
      clearIntervalUsers();
    }
  },
  { immediate: true }
);

const handleDropdownClick = async ({ key, fromTypificationParam = false } = {}) => {
  fromTypification.value = fromTypificationParam;
  if (key === "users") {
    openUserTransferModal.value = true;
    openQueueTransferModal.value = false;
    openQueueUserTransferModal.value = false;
  } else if (key === "queues") {
    openUserTransferModal.value = false;
    openQueueTransferModal.value = true;
    openQueueUserTransferModal.value = false;
  } else if (key === "queues-users") {
    openUserTransferModal.value = false;
    openQueueTransferModal.value = false;
    openQueueUserTransferModal.value = true;
  }

  const isOpen =
    key === "users" ||
    key === "queues" ||
    key === "userstype" ||
    key === "queuestype" ||
    key === "queues-users";

  if (isOpen) {
    emit("modalIsOpen", isOpen);

    setTimeout(() => {
      emit("modalIsOpen", false);
    }, 2000);
  }

  if (
    key === "queues" ||
    key === "queues-users" ||
    (fromTypificationParam && key === "queuestype")
  ) {
    await fetchTransferData();
  }
};

const fetchMonitorUsers = () => {
  store.dispatch("fetchMonitorUsers");
};

onMounted(() => {
  fetchMonitorUsers();
});

onUnmounted(() => {
  clearIntervalUsers();
});
</script>

<style lang="sass" scoped>
.button-transfer
  width: 100%
  display: flex
  align-items: center

  .icon
    font-size: 18px

.ant-dropdown-menu
  width: 190px !important
.color-icon
  color: $blue-6
  font-size: $font-medium
.color-icon-disabled
  color: $gray-6
  font-size: $font-medium
  cursor: not-allowed
:global(.ant-popover.popover-option)
  z-index: 1800 !important
</style>
